// src/app/hooks/usePromptAnalysis.ts
// Manages a single WebSocket connection for prompt analysis.
// Opens a WS, sends the prompt params, streams progress events, returns results.

import { useState, useCallback, useRef } from 'react';
import {
  AuthExpiredError,
  getStoredToken,
  notifyAuthExpired,
} from '@/api/api-fetch';

export type AnalysisStatus =
  | 'idle'
  | 'connecting'
  | 'running'
  | 'done'
  | 'error';

export interface AnalysisProgress {
  done: number;
  total: number;
  percent: number;
}

export interface AnalysisResult {
  recommendationRate: number;
  shareOfVoice: number;
  sentimentScore: number;
  averagePosition: number;
  trend: string;
  trendValue: string;
  aiInsight: string;
  sentimentThemes: { positive: string[]; negative: string[] };
  modelBreakdown: {
    modelId: string;
    modelName: string;
    recommendationRate: number;
    position: number;
    sentiment: number;
  }[];
  competitiveContext: {
    yourRank: number;
    totalCompetitors: number;
    topCompetitors: { name: string; shareOfVoice: number; position: number }[];
  };
  errors?: Record<string, string>;
}

interface UsePromptAnalysisReturn {
  status: AnalysisStatus;
  progress: AnalysisProgress | null;
  result: AnalysisResult | null;
  errorMessage: string | null;
  runAnalysis: (params: {
    promptId: string;
    promptText: string;
    intent: string;
    brandName: string;
    models: string[];
  }) => void;
  reset: () => void;
}

function getWsBase(): string {
  const apiBase = import.meta.env.VITE_API_BASE_URL || '';
  if (apiBase) return apiBase.replace(/^http/, 'ws');
  // Same-origin: derive from window.location
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}`;
}

export function usePromptAnalysis(): UsePromptAnalysisReturn {
  const [status, setStatus] = useState<AnalysisStatus>('idle');
  const [progress, setProgress] = useState<AnalysisProgress | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const reset = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    setStatus('idle');
    setProgress(null);
    setResult(null);
    setErrorMessage(null);
  }, []);

  const runAnalysis = useCallback(
    (params: {
      promptId: string;
      promptText: string;
      intent: string;
      brandName: string;
      models: string[];
    }) => {
      // Close any existing connection
      wsRef.current?.close();

      const token = getStoredToken();
      if (!token) {
        notifyAuthExpired('/api/ws/prompt-analysis');
        throw new AuthExpiredError('/api/ws/prompt-analysis');
      }
      const url = `${getWsBase()}/api/ws/prompt-analysis?token=${encodeURIComponent(token)}`;

      setStatus('connecting');
      setProgress(null);
      setResult(null);
      setErrorMessage(null);

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('running');
        ws.send(
          JSON.stringify({
            promptId:   params.promptId,
            promptText: params.promptText,
            intent:     params.intent,
            brandName:  params.brandName,
            models:     params.models,
          })
        );
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);

          switch (msg.type) {
            case 'analysis_started':
              setStatus('running');
              setProgress({ done: 0, total: msg.totalModels, percent: 0 });
              break;

            case 'progress':
              setProgress({
                done:    msg.done,
                total:   msg.total,
                percent: msg.percent,
              });
              break;

            case 'final_result':
              setResult(msg.data as AnalysisResult);
              setStatus('done');
              break;

            case 'error':
              setErrorMessage(msg.message || 'Analysis failed');
              setStatus('error');
              break;

            default:
              break;
          }
        } catch {
          // ignore malformed frames
        }
      };

      ws.onerror = () => {
        setErrorMessage('WebSocket connection failed. Check that the backend is running.');
        setStatus('error');
      };

      ws.onclose = () => {
        // If we closed without getting a result, mark as error
        setStatus((prev) => (prev === 'running' || prev === 'connecting' ? 'error' : prev));
      };
    },
    []
  );

  return { status, progress, result, errorMessage, runAnalysis, reset };
}